import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Globe, X, ChevronDown, Search, Check, Plus, Zap,
  Square, Layers, Image, FileText, Rocket
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'

const TEMPLATES = [
  { id: 'blank', name: 'Blank', Icon: Square, desc: 'Start from an empty canvas' },
  { id: 'restaurant', name: 'Restaurant', Icon: Globe, desc: 'Menu, hours, reservations' },
  { id: 'portfolio', name: 'Portfolio', Icon: Image, desc: 'Showcase work in a gallery' },
  { id: 'business', name: 'Business', Icon: Layers, desc: 'Services, team & contact' },
  { id: 'landing', name: 'Landing page', Icon: Rocket, desc: 'One-page conversion focus' },
]

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

const NewSite = () => {
  const navigate = useNavigate()
  const [selectedTemplate, setSelectedTemplate] = useState('restaurant')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [query, setQuery] = useState('')

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm()
  const selectedClientId = watch('client_id')

  const { data: clientsData, isLoading: clientsLoading } = useQuery(
    'clients',
    () => apiService.get('/clients').then(res => res.data)
  )
  const clients = clientsData?.clients || []
  const selectedClient = clients.find(c => String(c.id) === String(selectedClientId))

  const filteredClients = clients.filter(c =>
    (c.company_name || '').toLowerCase().includes(query.toLowerCase()) ||
    (c.user_name || '').toLowerCase().includes(query.toLowerCase())
  )

  const createSiteMutation = useMutation(
    (siteData) => apiService.post('/sites', siteData),
    {
      onSuccess: (response) => {
        toast.success('Site created successfully!')
        navigate(`/admin/editor?site=${response.data.id}`)
      },
      onError: (error) => {
        const msg = error.response?.data?.error || error.response?.data?.errors?.[0]?.msg || 'Failed to create site'
        toast.error(msg)
      }
    }
  )

  const onSubmit = (data) => {
    createSiteMutation.mutate({
      name: data.name,
      client_id: data.client_id,
      template_id: null,
      description: data.description || ''
    })
  }

  const initials = (client) => {
    if (!client) return '?'
    return (client.company_name || client.user_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
  }

  return (
    <div className="drawer-backdrop" style={{ justifyContent: 'center', alignItems: 'center' }}
      onClick={() => navigate('/admin/sites')}
    >
      <div className="card" style={{ width: 660, maxWidth: '94vw', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="icon-tile" style={{ width: 36, height: 36 }}><Globe style={{ width: 16, height: 16 }} /></span>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', margin: 0 }}>Create a new site</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>Pick a starting template and assign it to a client.</div>
          </div>
          <button className="site-action" onClick={() => navigate('/admin/sites')}><X style={{ width: 15, height: 15 }} /></button>
        </div>

        {/* Body */}
        <div style={{ padding: 22, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Field label="Site name">
            <input
              className="input"
              placeholder="e.g. Acme Marketing Site"
              {...register('name', { required: 'Site name is required' })}
              style={errors.name ? { borderColor: 'var(--danger)' } : {}}
            />
            {errors.name && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 2 }}>{errors.name.message}</span>}
          </Field>

          <Field label="Template">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 10 }}>
              {TEMPLATES.map(t => {
                const sel = selectedTemplate === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedTemplate(t.id)}
                    style={{
                      textAlign: 'left', padding: 13, borderRadius: 12,
                      border: `1.5px solid ${sel ? 'var(--border-accent)' : 'var(--border)'}`,
                      background: sel ? 'var(--accent-soft)' : 'var(--surface)',
                      transition: 'all 0.14s ease', position: 'relative',
                    }}
                  >
                    <span className="icon-tile" style={{ width: 30, height: 30, fontSize: 14, background: sel ? 'var(--accent)' : 'var(--surface-2)', color: sel ? 'white' : 'var(--text-muted)', border: 'none' }}>
                      <t.Icon style={{ width: 14, height: 14 }} />
                    </span>
                    <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 9 }}>{t.name}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.35 }}>{t.desc}</div>
                    {sel && <span style={{ position: 'absolute', top: 10, right: 10, color: 'var(--accent-3)' }}><Check style={{ width: 13, height: 13 }} /></span>}
                  </button>
                )
              })}
            </div>
          </Field>

          <Field label="Assign to client">
            {clientsLoading ? (
              <div style={{ padding: '10px 0' }}><LoadingSpinner size="sm" text="Loading clients…" /></div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input type="hidden" {...register('client_id', { required: 'Please select a client' })} />
                <button
                  type="button"
                  onClick={() => setDropdownOpen(v => !v)}
                  className="input"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                >
                  {selectedClient ? (
                    <>
                      <span className="avatar avatar-grad-1" style={{ width: 22, height: 22, fontSize: 9, flexShrink: 0 }}>{initials(selectedClient)}</span>
                      <span style={{ flex: 1, color: 'var(--text)' }}>{selectedClient.company_name || selectedClient.user_name}</span>
                    </>
                  ) : (
                    <span style={{ flex: 1, color: 'var(--text-faint)' }}>Select a client…</span>
                  )}
                  <ChevronDown style={{ width: 14, height: 14, color: 'var(--text-muted)', flexShrink: 0 }} />
                </button>
                {dropdownOpen && (
                  <div className="card" style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 10, boxShadow: 'var(--shadow-lg)', padding: 8 }}>
                    <div className="filter-bar" style={{ border: 'none', padding: '2px 6px 8px' }}>
                      <Search style={{ width: 14, height: 14, color: 'var(--text-muted)', flexShrink: 0 }} />
                      <input className="input" autoFocus placeholder="Search clients…" value={query} onChange={e => setQuery(e.target.value)} />
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {filteredClients.map((c, i) => (
                        <button
                          key={c.id}
                          type="button"
                          className="layer-row"
                          style={{ width: '100%' }}
                          onClick={() => { setValue('client_id', String(c.id)); setDropdownOpen(false); setQuery('') }}
                        >
                          <span className={`avatar avatar-grad-${(i % 4) + 1}`} style={{ width: 24, height: 24, fontSize: 9 }}>{initials(c)}</span>
                          <span style={{ flex: 1, textAlign: 'left' }}>
                            <span style={{ display: 'block', color: 'var(--text)', fontWeight: 500 }}>{c.company_name || c.user_name}</span>
                            <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{c.subscription_plan} · {c.site_count || 0} site{c.site_count !== 1 ? 's' : ''}</span>
                          </span>
                          {String(c.id) === String(selectedClientId) && <Check style={{ width: 13, height: 13, color: 'var(--accent-3)' }} />}
                        </button>
                      ))}
                      {filteredClients.length === 0 && <div style={{ padding: 14, textAlign: 'center', fontSize: 12, color: 'var(--text-faint)' }}>No clients found</div>}
                    </div>
                  </div>
                )}
                {errors.client_id && <span style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4, display: 'block' }}>{errors.client_id.message}</span>}
              </div>
            )}
          </Field>

          <Field label="Description" hint="optional">
            <textarea className="input" rows={3} style={{ resize: 'vertical', lineHeight: 1.6 }} placeholder="What is this site for?" {...register('description')} />
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Zap style={{ width: 12, height: 12, color: 'var(--accent-3)' }} /> Provisions instantly on a staging domain
          </span>
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={() => navigate('/admin/sites')}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit(onSubmit)}
            disabled={createSiteMutation.isLoading}
          >
            {createSiteMutation.isLoading ? <LoadingSpinner size="sm" /> : <><Plus style={{ width: 14, height: 14 }} /> Create site</>}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NewSite
